// =============================================================================
// Dialogs — promise-based replacements for native window.confirm /
// window.prompt / window.alert. Styled to match the app's dark UI;
// non-blocking; mobile-friendly (text inputs render with the right
// inputMode, no jankalert dialog over the viewport).
//
// Usage (anywhere in the component tree, no Provider needed):
//
//   const ok = await confirmDialog({
//     title:    'Delete pallet?',
//     message:  'KF49-2026-12345 will be soft-deleted.',
//     confirm:  'Delete',
//     tone:     'danger',
//   })
//   if (!ok) return
//
//   const reason = await promptDialog({
//     title:       'Reason for deleting?',
//     placeholder: 'e.g. duplicate, wrong run, test row',
//     allowEmpty:  true,
//   })
//   if (reason === null) return  // cancelled
//
//   await alertDialog({ title: 'Saved', message: '5 pallets updated.' })
//
// Implementation:
//   - Each call mounts a transient React root onto a host <div> appended
//     to <body>. The root unmounts when the dialog resolves so we don't
//     leak listeners. Avoids needing a top-level <DialogHost /> + global
//     state store; calls can fire from anywhere (event handlers,
//     mutationFns) without restructuring the tree.
//   - Resolution: confirm → boolean. prompt → string | null (null on
//     cancel; '' allowed if allowEmpty is true). alert → void.
// =============================================================================

import { useEffect, useId, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { AlertTriangle, Info, X } from 'lucide-react'
import clsx from 'clsx'
import { playExit } from '@/lib/motion'

type Tone = 'danger' | 'warning' | 'info'

interface BaseProps {
  title?:   string
  message?: string
  /** "Confirm" label. Defaults to "OK" for alert, "Confirm" for confirm. */
  confirm?: string
  /** "Cancel" label. Hidden on alert dialogs. */
  cancel?:  string
  tone?:    Tone
}

interface ConfirmProps extends BaseProps {
  message: string
  /** Lines that must not be read past — shown above the message in an amber
   *  block. For something ABSENT that the operator could otherwise approve
   *  without noticing (a pay run missing last run's allowance). */
  warnings?: string[]
}

function WarningBlock({ warnings }: { warnings?: string[] }) {
  if (!warnings || warnings.length === 0) return null
  return (
    <div role="alert" className="mb-3 rounded-lg border border-amber-700/70 bg-amber-950/50 p-2.5 space-y-1.5">
      {warnings.map(w => (
        <p key={w} className="text-sm font-semibold text-amber-200 whitespace-pre-line flex items-start gap-1.5">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden />
          <span>{w}</span>
        </p>
      ))}
    </div>
  )
}

interface PromptProps extends BaseProps {
  placeholder?: string
  defaultValue?: string
  /** Hint text under the input — small grey caption. */
  hint?: string
  /** When true, '' resolves to '' instead of null. Default false. */
  allowEmpty?: boolean
  /** input type — text (default) | number. */
  inputType?: 'text' | 'number'
  inputMode?: 'text' | 'numeric' | 'decimal'
}

interface AlertProps extends BaseProps {
  message: string
  /** A pointer under the message that opens in a NEW tab, so the screen behind
   *  the alert keeps its state. A plain anchor: an imperative dialog has its own
   *  React root, with no router. The caller decides whether the viewer may open it. */
  link?: { href: string; label: string }
}

interface ChoiceOption {
  value: string
  label: string
  /** Renders the button in the danger style (red). Default: neutral. */
  danger?: boolean
}

interface ChoiceProps extends BaseProps {
  message: string
  /** 2–3 real actions. Cancel is added automatically and always means "do nothing". */
  options: ChoiceOption[]
}

// --- presentational shell --------------------------------------------------

function DialogShell({
  tone, title, children, onClose, role, messageId,
}: {
  tone:    Tone
  title?:  string
  children: React.ReactNode
  onClose: () => void
  /** `alertdialog` for a message that interrupts and needs an answer (alert,
   *  confirm, choice); `dialog` for a form (prompt). */
  role:    'dialog' | 'alertdialog'
  /** The message paragraph's id, so a screen reader reads it on open. */
  messageId?: string
}) {
  const titleId = useId()
  const toneClasses: Record<Tone, { icon: typeof AlertTriangle; iconWrap: string }> = {
    danger:  { icon: AlertTriangle, iconWrap: 'bg-red-500/15    text-red-400'    },
    warning: { icon: AlertTriangle, iconWrap: 'bg-amber-500/15  text-amber-400'  },
    info:    { icon: Info,          iconWrap: 'bg-blue-500/15   text-blue-300'   },
  }
  const { icon: Icon, iconWrap } = toneClasses[tone]

  // Close on Escape — match native confirm/prompt behaviour.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    // The backdrop is a REDUNDANT convenience, not the keyboard path: Escape is
    // bound on `window` above (`if (e.key === 'Escape') onClose()`), so a
    // keyboard user closes this without ever reaching the backdrop. The rule
    // can only see handlers on the element, so it reports a mouse-only control
    // that is not one.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      data-theme="dark"
      className="fixed inset-0 z-dialog flex items-end sm:items-center justify-center bg-black/70 px-4 print:hidden motion-safe:animate-backdrop-in"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}
      // Bottom-sheet on phones so promptDialog's input + buttons sit above
      // the on-screen keyboard instead of relying on the browser to pan a
      // centered dialog. Inline env() — see PageHeader.
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1rem)' }}
    >
      {/* Announced as a dialog, named by its title and described by its
          message. Without these a screen reader met an unnamed block of text
          and buttons, and the page behind it was not marked as set aside. */}
      <div role={role} aria-modal="true" aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : 'Message'} aria-describedby={messageId}
        data-dialog-card className="surface-sunk border hairline rounded-2xl w-full max-w-md max-h-[90dvh] overflow-y-auto shadow-2xl motion-safe:animate-sheet-in motion-safe:sm:animate-dialog-in">
        <div className="flex items-start gap-3 px-5 pt-5">
          <span className={clsx('shrink-0 w-9 h-9 rounded-xl flex items-center justify-center', iconWrap)}>
            <Icon size={20} />
          </span>
          <div className="flex-1 min-w-0">
            {title && <h2 id={titleId} className="text-base font-semibold text-white leading-snug">{title}</h2>}
          </div>
          <button onClick={onClose} aria-label="Close" className="shrink-0 -m-2 w-11 h-11 flex items-center justify-center text-muted hover:text-gray-300">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

// --- confirm ----------------------------------------------------------------

function ConfirmDialogInternal({
  title, message, warnings, confirm = 'Confirm', cancel = 'Cancel', tone = 'warning',
  onResolve,
}: ConfirmProps & { onResolve: (v: boolean) => void }) {
  const messageId = useId()
  return (
    <DialogShell tone={tone} title={title} onClose={() => onResolve(false)} role="alertdialog" messageId={messageId}>
      <div className="px-5 pb-5 pt-3">
        <WarningBlock warnings={warnings} />
        <p id={messageId} className="text-sm text-gray-300 whitespace-pre-line">{message}</p>
        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={() => onResolve(false)}
            className="btn-sm surface hover:bg-gray-700 text-gray-200">
            {cancel}
          </button>
          <button
            autoFocus
            onClick={() => onResolve(true)}
            className={clsx(
              'btn-sm',
              tone === 'danger' ? 'bg-red-600 hover:bg-red-500 text-white'
                                 : 'bg-blue-600 hover:bg-blue-500 text-white',
            )}>
            {confirm}
          </button>
        </div>
      </div>
    </DialogShell>
  )
}

// --- prompt -----------------------------------------------------------------

function promptLabel(title?: string, placeholder?: string): string {
  return title ?? placeholder ?? 'Your answer'
}

// What a prompt resolves with, or null while it must not submit yet.
function promptAnswer(value: string, allowEmpty: boolean): string | null {
  const trimmed = value.trim()
  if (!allowEmpty && !trimmed) return null   // require a value
  return allowEmpty ? value : trimmed
}

function PromptHint({ hint }: { hint?: string }) {
  return hint ? <p className="text-xs text-muted">{hint}</p> : null
}

function PromptMessage({ id, message }: { id: string; message?: string }) {
  return message ? <p id={id} className="text-sm text-gray-300 whitespace-pre-line">{message}</p> : null
}

function PromptDialogInternal({
  title, message, placeholder, defaultValue = '', hint, allowEmpty = false,
  confirm = 'OK', cancel = 'Cancel', tone = 'info', inputType = 'text', inputMode = 'text',
  onResolve,
}: PromptProps & { onResolve: (v: string | null) => void }) {
  const [value, setValue] = useState(defaultValue)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const messageId = useId()
  const submit = () => {
    const answer = promptAnswer(value, allowEmpty)
    if (answer !== null) onResolve(answer)
  }

  return (
    <DialogShell tone={tone} title={title} onClose={() => onResolve(null)} role="dialog" messageId={message ? messageId : undefined}>
      <form
        onSubmit={e => { e.preventDefault(); submit() }}
        className="px-5 pb-5 pt-3 space-y-3">
        <PromptMessage id={messageId} message={message} />
        <input
          ref={inputRef}
          type={inputType}
          inputMode={inputMode}
          aria-label={promptLabel(title, placeholder)}
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2.5 rounded-lg surface border hairline-strong text-base text-white placeholder:text-gray-500 focus:border-blue-500 focus:outline-none"
        />
        <PromptHint hint={hint} />
        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={() => onResolve(null)}
            className="btn-sm surface hover:bg-gray-700 text-gray-200">
            {cancel}
          </button>
          <button
            type="submit"
            disabled={promptAnswer(value, allowEmpty) === null}
            className="btn-sm bg-blue-600 hover:bg-blue-500 text-white">
            {confirm}
          </button>
        </div>
      </form>
    </DialogShell>
  )
}

// --- alert ------------------------------------------------------------------

function AlertDialogInternal({
  title, message, link, confirm = 'OK', tone = 'info',
  onResolve,
}: AlertProps & { onResolve: () => void }) {
  const messageId = useId()
  return (
    <DialogShell tone={tone} title={title} onClose={onResolve} role="alertdialog" messageId={messageId}>
      <div className="px-5 pb-5 pt-3">
        <p id={messageId} className="text-sm text-gray-300 whitespace-pre-line">{message}</p>
        {link && (
          <p className="text-sm mt-2">
            <a href={link.href} target="_blank" rel="noreferrer" className="text-blue-300 underline underline-offset-2 min-h-11 inline-flex items-center">
              {link.label}<span className="sr-only"> (opens in a new tab)</span>
            </a>
          </p>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button
            autoFocus
            onClick={onResolve}
            className="btn-sm bg-blue-600 hover:bg-blue-500 text-white">
            {confirm}
          </button>
        </div>
      </div>
    </DialogShell>
  )
}

// --- choice -----------------------------------------------------------------
// For the "two real actions" case that confirmDialog must never encode as
// OK-vs-Cancel: every dismiss affordance (Escape / backdrop / X / Cancel)
// resolves null = do nothing; each action is its own explicit button.
// (Playbook §3.17 — born from the Orchard "Recalc ha" incident, where
// Escape ran the fill-empty bulk write.)

function ChoiceDialogInternal({
  title, message, options, cancel = 'Cancel', tone = 'warning',
  onResolve,
}: ChoiceProps & { onResolve: (v: string | null) => void }) {
  const messageId = useId()
  return (
    <DialogShell tone={tone} title={title} onClose={() => onResolve(null)} role="alertdialog" messageId={messageId}>
      <div className="px-5 pb-5 pt-3">
        <p id={messageId} className="text-sm text-gray-300 whitespace-pre-line">{message}</p>
        <div className="flex flex-col gap-2 mt-5">
          {options.map(o => (
            <button
              key={o.value}
              onClick={() => onResolve(o.value)}
              className={clsx(
                'btn-sm w-full justify-center',
                o.danger ? 'bg-red-600 hover:bg-red-500 text-white'
                         : 'bg-blue-600 hover:bg-blue-500 text-white',
              )}>
              {o.label}
            </button>
          ))}
          <button
            onClick={() => onResolve(null)}
            className="btn-sm w-full justify-center surface hover:bg-gray-700 text-gray-200">
            {cancel}
          </button>
        </div>
      </div>
    </DialogShell>
  )
}

// --- mount helper ----------------------------------------------------------

// How many imperative dialogs are currently mounted. <Modal> reads this to
// stand down from Escape while one is up: these dialogs listen on WINDOW and
// a Modal listens on DOCUMENT, and document-phase listeners run first — so
// without this the Modal would eat the Escape, close ITSELF, and leave the
// confirm it had raised orphaned on top of a closing screen.
let openImperativeDialogs = 0

export function imperativeDialogOpen(): boolean {
  return openImperativeDialogs > 0
}

function mountDialog<T>(render: (resolve: (v: T) => void) => React.ReactElement): Promise<T> {
  return new Promise<T>(resolve => {
    const host = document.createElement('div')
    document.body.appendChild(host)
    const root = createRoot(host)
    openImperativeDialogs++

    // Idempotent: a second Escape during the exit animation must not
    // decrement the counter again and under-count another open dialog.
    let done = false
    const cleanup = (v: T) => {
      if (done) return
      done = true
      openImperativeDialogs = Math.max(0, openImperativeDialogs - 1)
      // Resolve BEFORE animating: the caller proceeds at once (a confirm
      // before mutate() starts the save and its 60s watchdog); the exit is
      // only decoration for the dialog going away.
      resolve(v)
      // No second tap may land on the fading dialog.
      host.style.pointerEvents = 'none'
      const backdrop = host.firstElementChild as HTMLElement | null
      const card = backdrop?.querySelector<HTMLElement>('[data-dialog-card]') ?? null
      void playExit(backdrop, card).then(() => {
        // setTimeout 0 so the click event finishes propagating before we
        // unmount — otherwise React fires "Cannot read setState on an
        // unmounted component" if the resolver triggers more state.
        setTimeout(() => {
          root.unmount()
          host.remove()
        }, 0)
      })
    }

    root.render(render(cleanup))
  })
}

// --- public API -------------------------------------------------------------

export function confirmDialog(props: ConfirmProps): Promise<boolean> {
  return mountDialog<boolean>(resolve =>
    <ConfirmDialogInternal {...props} onResolve={resolve} />
  )
}

export function choiceDialog(props: ChoiceProps): Promise<string | null> {
  return mountDialog<string | null>(resolve =>
    <ChoiceDialogInternal {...props} onResolve={resolve} />
  )
}

// A save that needs a reason asks it BEFORE mutate(), never inside the
// mutationFn: the save overlay's 60s stuck-save watchdog starts at mutate(), so
// a prompt still open after a minute reports a failed save for a write that has
// not started. (A confirm DECLINED inside a mutationFn is onlyWhenDone's case.)
export function promptDialog(props: PromptProps): Promise<string | null> {
  return mountDialog<string | null>(resolve =>
    <PromptDialogInternal {...props} onResolve={resolve} />
  )
}

export function alertDialog(props: AlertProps): Promise<void> {
  // Danger alerts are the "operator hit a wall" moments — count them per
  // screen for the UX advisor. Title only (digit-stripped in the tracker);
  // the message text never leaves this device through telemetry.
  // ⚠️ LAZY ON PURPOSE — do not turn this back into a static import.
  //
  // lib/uiTelemetry imports lib/supabase, which THROWS AT MODULE LOAD when the
  // env is absent. A static import here put that on the critical path of
  // <Modal/> (Dialogs is imported by 253 files), so the app's most-used shell
  // could not render without the data client. Nothing noticed, because the app
  // always has env — it surfaced only when a component workbench tried to
  // render <Modal/> in isolation and the module graph threw before React ran.
  //
  // Fire-and-forget: a danger alert must open whether or not telemetry loads.
  if (props.tone === 'danger') {
    void import('@/lib/uiTelemetry')
      .then(m => m.trackErrorDialog(props.title))
      .catch(() => {})   // telemetry is never worth failing an alert over
  }
  return mountDialog<void>(resolve =>
    <AlertDialogInternal {...props} onResolve={() => resolve()} />
  )
}
